import React from "react";
import { CalendarDays, Clock, ListChecks, Award, Smile, List } from "lucide-react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import locales from "../../utils/locales";
import parseDuration from "../../utils/parseDuration";

// --- Helper: Format ชั่วโมง/นาที ---
function formatHour(hourFloat, lang = "th") {
  if (hourFloat == null || isNaN(hourFloat) || hourFloat < 0) return lang === "th" ? "-" : "-";
  const floatVal = Number(hourFloat);
  const hour = Math.floor(floatVal);
  const min = Math.round((floatVal - hour) * 60);

  if (hour === 0 && min === 0) return lang === "th" ? "0 นาที" : "0 min";
  if (hour === 0) return lang === "th" ? `${min} นาที` : `${min} min`;
  if (min === 0) return lang === "th" ? `${hour} ชั่วโมง` : `${hour} hr`;
  return lang === "th"
    ? `${hour} ชั่วโมง ${min} นาที`
    : `${hour} hr ${min} min`;
}

// --- Helper: Group duration by category (input: activities + categories) ---
function groupDurationPerCategory(activities, categories, lang) {
  if (!activities || !categories) return [];
  const catDur = {};
  activities.forEach(a => {
    if (!a.category) return;
    catDur[a.category] = (catDur[a.category] || 0) + parseDuration(a.duration);
  });
  return categories
    .filter(cat => catDur[cat.value] > 0)
    .map(cat => ({
      id: cat.label[lang],
      label: cat.label[lang],
      value: +(catDur[cat.value] / 60).toFixed(2),
    }));
}

// --- Helper: Bar chart per activity (activityCountPerCat) ---
function getBarData(summary) {
  if (!summary || !summary.activityCountPerCat) return [];
  return summary.activityCountPerCat
    .filter(cat => cat.value > 0)
    .map(cat => ({
      activity: cat.label,
      value: cat.value
    }));
}


export default function DailyCard({ date, summary = {}, activities = [], categories = [], lang = "th" }) {
  const t = locales[lang];
  const barData = getBarData(summary);

  // Pie chart ใช้เวลาต่อหมวด (ชั่วโมง)
  // ถ้ามี activities/categores props → group ใหม่
  const pieData = (activities.length && categories.length)
    ? groupDurationPerCategory(activities, categories, lang)
    : [];

  // Pie colors
  const pieColors = [
    "#f6bedb", "#ffe29a", "#b8b4fa", "#b2e2e2",
    "#f7c68e", "#c0e6b2", "#d4d2fc", "#dadada"
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 flex flex-col">
      {/* วันที่ */}
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="text-indigo-500" size={22} />
        <span className="font-bold text-lg text-neutral-800">{date}</span>
      </div>

      {/* Grid: Summary หลัก */}
      <div className="grid grid-cols-2 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-blue-400" />
          <span>{t.total_time}:</span>
          <span className="font-semibold text-blue-700">
            {formatHour(Number(summary.totalHour), lang)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-pink-400" />
          <span>{t.top_category}:</span>
          <span className="font-semibold text-pink-600">{summary.topCategory || "-"}</span>
        </div>
        <div className="flex items-center gap-2 col-span-2">
          <Award size={18} className="text-green-400" />
          <span>{t.highlight_activity}:</span>
          <span
            className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium border border-green-100"
            style={{ minWidth: "1.5rem", display: "inline-block" }}
          >
            {summary.topActivity || "-"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Smile size={18} className="text-yellow-400" />
          <span>{t.mood || "Mood"}:</span>
          <span className="font-semibold text-yellow-600">{summary.avgMood || "-"}</span>
        </div>
        <div className="flex items-center gap-2">
          <List size={18} className="text-cyan-400" />
          <span>{t.activities_count || "Activities"}:</span>
          <span className="font-semibold text-cyan-700">{summary.activityCount || "-"}</span>
        </div>
      </div>

      {/* Visualization: Bar Chart */}
      {barData.length > 0 && (
        <div className="w-full h-48 mt-3">
          <div className="font-bold text-gray-700 mb-2 text-sm">
            {lang === "th" ? "กิจกรรมวันนี้" : "Today's Activities"}
          </div>
          <ResponsiveBar
            data={barData}
            keys={["value"]}
            indexBy="activity"
            margin={{ top: 10, right: 10, bottom: 40, left: 50 }}
            padding={0.3}
            colors={{ scheme: "pastel1" }}
            axisBottom={{
              tickRotation: 0,
              legend: lang === "th" ? "กิจกรรม" : "Activity",
              legendPosition: "middle",
              legendOffset: 32,
            }}
            axisLeft={{
              legend: lang === "th" ? "จำนวน (ครั้ง)" : "Count",
              legendPosition: "middle",
              legendOffset: -40,
            }}
            enableGridY={false}
            borderRadius={4}
            labelSkipWidth={16}
            labelSkipHeight={12}
            labelTextColor="#222"
            theme={{
              tooltip: { container: { fontSize: 13 } },
              labels: { text: { fontWeight: 500 } }
            }}
            animate={true}
            isInteractive={true}
            tooltip={({ indexValue, value }) =>
              <span>{indexValue}: <b>{value}</b></span>
            }
          />
        </div>
      )}

      {/* Visualization: Pie Chart (Time by Category - ชั่วโมง) */}
      {pieData.length > 0 && (
        <>
          <div className="w-full h-44 mt-6 mb-1">
            <div className="font-bold text-gray-700 mb-2 text-sm">
              {lang === "th" ? "แบ่งเวลาแต่ละหมวดวันนี้" : "Time Distribution by Category"}
            </div>
            <ResponsivePie
              data={pieData}
              margin={{ top: 20, right: 40, bottom: 30, left: 40 }}
              innerRadius={0.5}
              padAngle={1.5}
              cornerRadius={4}
              activeOuterRadiusOffset={8}
              colors={pieColors}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor="#222"
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLabelsSkipAngle={12}
              arcLabelsTextColor="#222"
              enableArcLabels={true}
              valueFormat={v => `${v} ${lang === "th" ? "ชม." : "hrs"}`}
              legends={[]} // ไม่โชว์ legend auto
              tooltip={({ datum }) =>
                <span>
                  <b>{datum.label}</b>: {datum.value} {lang === "th" ? "ชม." : "hrs"}
                </span>
              }
            />
          </div>
          {/* Badge legend (สีตรง slice) */}
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {pieData.map((cat, i) => (
              <span
                key={cat.label}
                className="px-3 py-1 rounded-xl bg-gray-50 text-neutral-800 text-sm font-medium flex items-center gap-1 border"
                style={{
                  borderColor: "#ececec",
                }}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full mr-1"
                  style={{ background: pieColors[i % pieColors.length] }}
                />
                <span className="font-semibold">{cat.label}</span>:
                <span>{cat.value} {lang === "th" ? "ชม." : "hrs"}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}